import { useEffect } from "react";

interface MobileOverlayProps {
  open: boolean;
  onClose: () => void;
  /** "left" → slides from the left (files tree), "right" → slides from the right */
  side?: "left" | "right";
  widthClass?: string;
  children: React.ReactNode;
  /** Optional backdrop opacity (0–1). Defaults to 0.3. */
  backdropOpacity?: number;
}

/**
 * Full-height slide-in overlay for mobile only. Backdrop dismisses.
 * Renders nothing (no portal) when closed. Desktop callers should bypass
 * this component entirely and render their panel inline.
 *
 * ESC closes. Body scroll locked while open. Focuses the first focusable
 * child on open so keyboard users can immediately act.
 */
export function MobileOverlay({
  open,
  onClose,
  side = "left",
  widthClass = "w-[85vw] max-w-[360px]",
  children,
  backdropOpacity = 0.3,
}: MobileOverlayProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div
        className="absolute inset-0 transition-opacity"
        style={{
          background: `rgba(26, 18, 16, ${backdropOpacity})`,
          animation: "fadeIn 180ms ease-out",
        }}
        onClick={onClose}
      />
      <div
        className={`absolute top-0 bottom-0 ${side === "left" ? "left-0" : "right-0"} ${widthClass} flex flex-col`}
        style={{
          background: "var(--surface)",
          borderRight: side === "left" ? "1px solid var(--blt)" : undefined,
          borderLeft: side === "right" ? "1px solid var(--blt)" : undefined,
          boxShadow:
            side === "left"
              ? "4px 0 24px rgba(26, 18, 16, 0.12)"
              : "-4px 0 24px rgba(26, 18, 16, 0.12)",
          animation: `${side === "left" ? "slideInLeft" : "slideInRight"} 220ms cubic-bezier(0.22, 0.61, 0.36, 1)`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
